import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { SessionService } from './session.service';
import { CHARACTER, NULL_UUID, SELECTED_PROFILE_ID } from '@storees/shared-constants';
import { v4 as uuidv4 } from 'uuid';
import { type components, ProfileGender } from '@storees/api-types';
import { AuthService } from './auth-service';
import { ProfileCreationStateService } from './profile-creation-state.service';
import { DexieProfilesService } from './dexie/dexie-profiles.service';
import { logger } from '../utils/logger';

export type Profile = components['schemas']['Profile'];
export type MinimalProfile = Omit<Profile, 'userId'>;

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private profileCreationStateService = inject(ProfileCreationStateService);
  private dexieProfilesService = inject(DexieProfilesService);

  public static readonly ADD_NEW_PROFILE: MinimalProfile = {
    id: NULL_UUID,
    name: 'Add New',
    birthdate: '1970-01-01T00:00:00Z',
    gender: ProfileGender.Female,
    profileImage: 'assets/icons/md-add.svg',
    narratorId: NULL_UUID,
    defaultNarrativeStyle: null,
  };

  // BehaviorSubject to hold the array of profiles and emit changes
  profilesSubject: BehaviorSubject<MinimalProfile[]> = new BehaviorSubject<MinimalProfile[]>([]);

  // Subject to trigger refresh of profiles
  private refreshTrigger = new BehaviorSubject<void>(undefined);

  // Public observable for components to subscribe to profile changes
  public profiles$: Observable<MinimalProfile[]>;

  constructor() {

    // Create a reactive stream that refreshes whenever refreshTrigger emits
    this.profiles$ = this.refreshTrigger.pipe(
      switchMap(() => this.dexieProfilesService.getProfiles()),
      tap((profiles) => {
        this.profilesSubject.next(profiles);
      })
    );

    // Initialize profiles from ProfilesService
    this.loadProfilesFromDexie().then(() => {
      console.log(
        'ProfileService initialized, profiles loaded:',
        this.profilesSubject.getValue()
      );
    });
  }

  /**
   * Loads profiles from Dexie database via ProfilesService.
   * @returns Promise that resolves when profiles are loaded
   */
  private async loadProfilesFromDexie(): Promise<void> {
    try {
      // Trigger a refresh to load profiles
      this.refreshTrigger.next();
    } catch (error) {
      console.error('Error loading profiles from Dexie:', error);
      this.profilesSubject.next([]);
    }
  }

  /**
   * Refresh profiles from the database
   */
  public refreshProfiles(): void {
    console.log('Refreshing profiles from database');
    this.refreshTrigger.next();
  }

  /**
   * Generates a unique ID for a new profile.
   * @returns A unique string ID.
   */
  private generateId(): string {
    return uuidv4();
  }

  /**
   * Initializes or replaces the entire list of profiles.
   * Useful for setting initial data or resetting profiles.
   * @param profiles An array of MinimalProfile objects.
   */
  public async setInitialProfiles(profiles: MinimalProfile[]): Promise<void> {
    // Ensure all provided profiles have an ID, assign if not.
    const profilesWithIds = profiles.map((p) => ({
      ...p,
      id: p.id == null ? this.generateId() : p.id, // Use == null to catch undefined and null
    }));

    const jwtToken = await this.authService.getSessionJwtToken();

    // Add each profile locally, then run one server sync (avoid N blocking HTTP round-trips).
    for (const profile of profilesWithIds) {
      await firstValueFrom(
        this.dexieProfilesService.addProfile(jwtToken, profile, { awaitSync: false })
      );
    }
    try {
      await this.dexieProfilesService.syncProfiles(jwtToken);
    } catch (error) {
      console.error('ProfileService: Profile sync failed during setInitialProfiles:', error);
    }

    // Refresh the profiles list
    this.refreshProfiles();
  }

  /**
   * Lists all current profiles.
   * @returns An Observable emitting the array of profiles.
   */
  public listAllProfiles(): Observable<MinimalProfile[]> {
    return this.profiles$;
  }

  /**
   * Adds a new profile to the list.
   * The `id` for the new profile will be generated automatically.
   * @param newProfileData An object containing profile data, excluding the 'id'.
   * @returns The newly created MinimalProfile object with its generated ID.
   */
  public async addProfile(newProfileData: MinimalProfile): Promise<MinimalProfile> {
    if (
      !newProfileData.name ||
      !newProfileData.gender ||
      !newProfileData.profileImage
    ) {
      console.error(
        'Cannot add profile: Name, age, gender, and image are required.'
      );
      throw new Error('Required profile fields are missing.');
    }

    const jwtToken = await this.authService.getSessionJwtToken();

    try {
      const profileToAdd: MinimalProfile = {
        ...newProfileData,
        id: (newProfileData.id === NULL_UUID) ? newProfileData.id : this.generateId(),
      };
      // Persist locally first; server sync runs in the background so create UI is not blocked.
      const profileId = await firstValueFrom(
        this.dexieProfilesService.addProfile(jwtToken, profileToAdd, {
          awaitSync: false,
          storyLists: false,
        })
      );

      // Never persist the "Add New" list sentinel as the active library profile (breaks API + imports).
      if (
        this.profileCreationStateService.getProfileNavigationRoute() !== CHARACTER &&
        profileToAdd.id !== NULL_UUID
      ) {
        await this.sessionService.setPersistent('selectedProfile', profileToAdd);
        await this.sessionService.setPersistent(SELECTED_PROFILE_ID, profileToAdd.id);
      }

      console.log('Profile successfully added with ID:', profileId);

      // Refresh the profiles list to notify all subscribers
      this.refreshProfiles();

      return profileToAdd;
    } catch (error) {
      console.error('Failed to add profile:', error);
      throw error;
    }
  }

  /**
   * Updates an existing profile.
   * The profile to update is identified by its 'id'.
   * @param updatedProfile The MinimalProfile object with updated information. Must include the 'id'.
   * @returns Promise<boolean> that resolves to true if the update was successful, false if the profile was not found.
   */
  public async updateProfile(updatedProfile: MinimalProfile): Promise<boolean> {
    if (updatedProfile.id == null) {
      console.error('Cannot update profile: ID is missing.');
      return false;
    }

    try {
      const jwtToken = await this.authService.getSessionJwtToken();
      await firstValueFrom(
        this.dexieProfilesService.updateProfile(updatedProfile.id, jwtToken, updatedProfile)
      );

      // Refresh the profiles list to notify all subscribers
      this.refreshProfiles();

      return true;
    } catch (error) {
      console.warn(
        `ProfileService: Profile with ID ${updatedProfile.id} not found for update.`,
        error
      );
      return false;
    }
  }

  /**
   * Deletes a profile from the list by its ID.
   * @param profileId The ID of the profile to delete.
   * @returns Promise<boolean> that resolves to true if the deletion was successful, false if the profile was not found.
   */
  public async deleteProfile(profileId: string): Promise<boolean> {
    if (profileId == null) {
      console.error('Cannot delete profile: ID is missing or invalid.');
      return false;
    }

    try {
      const jwtToken = await this.authService.getSessionJwtToken();
      await firstValueFrom(await this.dexieProfilesService.deleteProfile(profileId, jwtToken));
      await this.sessionService.setPersistent(SELECTED_PROFILE_ID, NULL_UUID);

      // Refresh the profiles list to notify all subscribers
      this.refreshProfiles();

      return true;
    } catch (error) {
      console.warn(
        `ProfileService: Profile with ID ${profileId} not found for deletion.`,
        error
      );
      return false;
    }
  }

  /**
   * Gets a single profile by its ID.
   * @param profileId The ID of the profile to retrieve.
   * @returns An Observable emitting the found MinimalProfile or undefined if not found.
   */
  public getProfileById(profileId: string): Observable<MinimalProfile | undefined> {
    return this.dexieProfilesService.getProfile(profileId);
  }

  /**
   * Deletes all profiles from the service and Dexie database.
   */
  public async deleteAllProfilesFromLocalAndServer(): Promise<void> {
    const currentProfiles = await firstValueFrom(this.dexieProfilesService.getProfiles());
    for (const profile of currentProfiles || []) {
      if (profile.id === NULL_UUID) {
        continue;
      }

      if (profile.id) {
        const jwtToken = await this.authService.getSessionJwtToken();
        await firstValueFrom(await this.dexieProfilesService.deleteProfile(profile.id, jwtToken));
      }
    }
    await this.sessionService.setPersistent(SELECTED_PROFILE_ID, NULL_UUID);
    await this.sessionService.setPersistent('selectedProfile', '');
    this.profilesSubject.next([]);
    this.refreshProfiles();
  }

  public async deleteAllProfilesFromLocal(): Promise<void> {
    const currentProfiles = await firstValueFrom(this.dexieProfilesService.getProfiles());
    for (const profile of currentProfiles || []) {
      if (profile.id === NULL_UUID) {
        continue;
      }
      if (profile.id) {
        await this.dexieProfilesService.deleteLocalProfileOnly(profile.id);
      }
    }
    await this.sessionService.setPersistent(SELECTED_PROFILE_ID, NULL_UUID);
    await this.sessionService.setPersistent('selectedProfile', '');
    this.profilesSubject.next([]);
    this.refreshProfiles();
  }

  public async setProfiles(
    sessionService: SessionService,
    selectedProfileSubject?: BehaviorSubject<MinimalProfile | null>
  ): Promise<void> {
    console.log('ProfileService: Starting setProfiles');

    // First, ensure profiles are loaded from Dexie
    await this.loadProfilesFromDexie();

    // If there's a stored profile ID, try to load that profile
    const storedProfileId = await sessionService.getPersistent(SELECTED_PROFILE_ID);
    if (storedProfileId && storedProfileId !== NULL_UUID && selectedProfileSubject) {
      this.getProfileById(storedProfileId).subscribe(profile => {
        if (profile) {
          console.log('ProfileService: Setting stored profile:', profile.name);
          selectedProfileSubject.next(profile);
        }
      });
    }
    console.log('ProfileService: Completed setProfiles');
  }

  /**
   * If session points at the "Add New" sentinel or is empty but Dexie has real profiles,
   * pick the first real profile. Fixes startup `addProfile(ADD_NEW_PROFILE)` previously
   * persisting NULL_UUID as {@link SELECTED_PROFILE_ID}.
   */
  public async repairSelectedProfileIfInvalid(): Promise<void> {
    const id = await this.sessionService.getPersistent(SELECTED_PROFILE_ID);
    if (id && id !== NULL_UUID) {
      return;
    }
    const profiles = await firstValueFrom(this.dexieProfilesService.getProfiles());
    const firstReal = profiles.find((p) => p.id && p.id !== NULL_UUID);
    if (!firstReal?.id) {
      logger.warn(
        '[IMPORT_SHARED_STORY] repairSelectedProfileIfInvalid — no real profile in Dexie to select',
        { storedId: id ?? null },
      );
      return;
    }
    await this.sessionService.setPersistent(SELECTED_PROFILE_ID, firstReal.id);
    logger.warn(
      '[IMPORT_SHARED_STORY] repairSelectedProfileIfInvalid — SELECTED_PROFILE_ID repaired',
      { profileIdSuffix: firstReal.id.slice(-12) },
    );
  }
}
